import React from "react";
import PropTypes from "prop-types";
import { Link, generatePath } from "react-router-dom";

import { UIChip } from "../../ui";
import TruncatedWithTooltip from "../../TruncatedWithTooltip";
import { clx } from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  ACTIVITY_MODULE_COLOR,
  formatActivityDateTime,
} from "../../Dashboard/RecentActivityLog/constants";
import "./ActivityLogTable.scss";

const DASH = "—";

/** Text truncated after `lines` lines, with a tooltip when it doesn't fit. */
const Cell = ({ text, className, lines }) => (
  <TruncatedWithTooltip title={text} lines={lines}>
    <span className={className}>{text || DASH}</span>
  </TruncatedWithTooltip>
);

Cell.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string.isRequired,
  lines: PropTypes.number,
};

Cell.defaultProps = {
  text: "",
  lines: 1,
};

/**
 * Full activity log table (admin + company portals) — Time · Performed By
 * (role beneath) · Company (`showCompany`) · Action · Module tag. Company
 * shows "—" for platform-level entries. Read-only.
 */
const ActivityLogTable = ({
  rows,
  labels,
  modules,
  lang,
  showCompany,
}) => (
  <div
    className={clx(
      "activity-log-table",
      !showCompany && "activity-log-table--no-company",
    )}
  >
    <div className="activity-log-table__grid" role="table">
      <div
        className="activity-log-table__row activity-log-table__row--header"
        role="row"
      >
        <span role="columnheader">{labels.columns.time}</span>
        <span role="columnheader">{labels.columns.actor}</span>
        {showCompany ? (
          <span role="columnheader">{labels.columns.company}</span>
        ) : null}
        <span role="columnheader">{labels.columns.action}</span>
        <span role="columnheader">{labels.columns.module}</span>
      </div>
  
      {rows.map((row) => (
        <div key={row.id} className="activity-log-table__row" role="row">
          <span className="activity-log-table__time" role="cell">
            {formatActivityDateTime(row.createdAt, lang)}
          </span>
          <span className="activity-log-table__actor" role="cell">
            <Cell text={row.actor.name} className="activity-log-table__name" />
            {row.actor.role ? (
              <Cell text={row.actor.role} className="activity-log-table__role" />
            ) : null}
          </span>
          {showCompany ? (
            <span role="cell">
              {row.companyId ? (
                <TruncatedWithTooltip title={row.company}>
                  <Link
                    className="activity-log-table__strong activity-log-table__company-link"
                    to={generatePath(ROUTE_PATHS.companiesListCompanyDetails, {
                      id: row.companyId,
                    })}
                  >
                    {row.company}
                  </Link>
                </TruncatedWithTooltip>
              ) : (
                <Cell text={row.company} className="activity-log-table__strong" />
              )}
            </span>
          ) : null}
          <span role="cell">
            <Cell
              text={row.action}
              className="activity-log-table__action"
              lines={2}
            />
          </span>
          <span role="cell">
            {row.module ? (
              <UIChip
                color={ACTIVITY_MODULE_COLOR[row.module]}
                label={modules[row.module]}
              />
            ) : (
              DASH
            )}
          </span>
        </div>
      ))}
    </div>
  </div>
);

ActivityLogTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      createdAt: PropTypes.string.isRequired,
      actor: PropTypes.shape({
        name: PropTypes.string.isRequired,
        role: PropTypes.string,
      }).isRequired,
      /** `null` for a platform-level entry. */
      company: PropTypes.string,
      /** Links the company name to its profile; `null` with `company`. */
      companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      /** Undefined for a `null` / unknown API module — shows a dash. */
      module: PropTypes.oneOf(Object.keys(ACTIVITY_MODULE_COLOR)),
      action: PropTypes.string.isRequired,
    }),
  ).isRequired,
  labels: PropTypes.shape({
    columns: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  modules: PropTypes.objectOf(PropTypes.string).isRequired,
  lang: PropTypes.string.isRequired,
  /** Company column — off on the company portal, where every entry is the user's company. */
  showCompany: PropTypes.bool,
};

ActivityLogTable.defaultProps = {
  showCompany: true,
};

export default ActivityLogTable;
